import React, { useEffect, useMemo, useState } from 'react';
import { withTranslation } from 'react-i18next';
import {
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Row,
  Spinner,
} from 'reactstrap';
import Breadcrumb from '../../Components/Common/BreadCrumb';
import TableContainer from '../../Components/Common/TableContainer';
import { useDispatch, useSelector } from 'react-redux';
import { getUsers } from '../../slices/thunks';
import { formatDate } from '../../helpers/format';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import _, { first, isEmpty } from 'lodash';
import CreateUserForm from './Create';
import EditUserForm from './Edit';
import UserImporter from './UserImporter';
import { Can } from '../../helpers/casl';
import Drawer from '../../Components/Common/Drawer';
import useQsParams from '../../Components/Hooks/QueryString';
import QueryString from 'qs';
import Search from '../../Components/Common/Search';
import { getFilters } from '../../Components/Common/Filter';

const User = (props) => {
  document.title = 'Usuários | Opencashback';

  const [createUserModal, setCreateUserModal] = useState(false);
  const [editUserModal, setEditUserModal] = useState(false);
  const [showImporter, setShowImporter] = useState(false);
  const [user, setUser] = useState({});

  const { qs, searchParams, setSearchParams, setSearchParamsAsObject } =
    useQsParams();

  const toggleCreateUser = () => {
    setCreateUserModal(!createUserModal);
  };

  const toggleEditUser = () => {
    setEditUserModal(!editUserModal);
  };

  const dispatch = useDispatch();

  const users = useSelector((state) => state.User.users);
  const meta = useSelector((state) => state.User.meta);
  const isLoading = useSelector((state) => state.User.isLoading);

  const getData = () => {
    const params = {
      page: searchParams.get('page') ?? 1,
      order: 'desc',
    };

    const appliedFilters = getFilters([], [
      { field: 'name', type: 'like' },
      { field: 'email', type: 'like' },
    ]);

    if (!isEmpty(appliedFilters)) {
      params.filters = appliedFilters;
    }

    const paramsString = QueryString.stringify(params, { addQueryPrefix: true });

    dispatch(getUsers(paramsString));
  };

  useEffect(() => {
    getData();
  }, [searchParams.toString()]);

  const getStatusBadgeClasses = (status) => {
    switch (status) {
      case 'active':
        return 'bg-success-subtle text-success';
      case 'inactive':
        return 'bg-warning-subtle text-warning';
      default:
        return 'bg-danger-subtle text-danger';
    }
  };

  const columns = useMemo(
    () => [
      {
        Header: props.t('name'),
        accessor: 'name',
        filterable: false,
        Cell: (info) => info.row.original.name,
      },
      {
        Header: props.t('email'),
        accessor: 'email',
        filterable: false,
        Cell: (info) => info.row.original.email,
      },
      {
        Header: props.t('status'),
        accessor: 'status',
        filterable: false,
        Cell: (info) => {
          const status = info.row.original.status;
          const text = props.t(`user-status-${status}`);

          return (
            <React.Fragment>
              <span
                className={`badge rounded-pill ${getStatusBadgeClasses(status)}`}
              >
                {text}
              </span>
            </React.Fragment>
          );
        },
      },
      {
        Header: props.t('created-at'),
        accessor: 'created_at',
        filterable: false,
        Cell: (info) => formatDate(info.row.original.created_at),
      },
      {
        Header: props.t('permission'),
        accessor: 'permission',
        filterable: false,
        Cell: (info) => {
          const role = first(info.row.original.roles)?.name;
          return role || '-';
        },
      },
      {
        Header: props.t('store'),
        accessor: 'stores',
        Cell: ({ value, row }) => {
          const isAdmin = row?.original?.isAdmin;
          const data = isAdmin ? [{ name: 'Todas' }] : value;
          if (!value?.length && !isAdmin) return '-';

          return (
            <ul className="list-group ms-3">
              {data.map((rule, key) => (
                <li key={key}>{rule.name}</li>
              ))}
            </ul>
          );
        },
        reportProps: {
          isArray: true,
        },
      },
      {
        Header: props.t('actions'),
        Cell: (info) => {
          return (
            <ul className="list-inline hstack gap-2 mb-0">
              <Can I="update" a="account_users">
                <li className="list-inline-item">
                  <button
                    type="button"
                    className="bg-transparent p-0 border-0"
                    onClick={() => {
                      const { id, email, name, isAdmin } = info.row.original;
                      const { roles, stores, status } = info.row.original;

                      const userData = { id, email, name, isAdmin };
                      const userInfo = { roles, stores, status };
                      const payload = { ...userData, ...userInfo };

                      setSearchParamsAsObject({
                        create: '',
                        edit: encodeURI(JSON.stringify(payload)),
                      });
                    }}
                  >
                    <i className="ri-pencil-fill align-bottom text-muted" />
                  </button>
                </li>
              </Can>
            </ul>
          );
        },
      },
    ],
    []
  );

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Breadcrumb title="Usuários" pageTitle="Configurações" />
          <Row>
            <Col lg={12}>
              <Card>
                <CardHeader className="border-0">
                  <Row className="row-cols-1 row-cols-sm-2 row-cols-md-12 g-2 g-lg-3">
                    <Col xl={2}>
                      <Search
                        placeholder={props.t('name')}
                        value={qs?.get('name')?.replace(/%/g, '')}
                        onSubmit={({ id }) => {
                          const value = _.trim(id);
                          setSearchParamsAsObject({
                            name: id ? `%${value}%` : '',
                            page: 1,
                          });
                        }}
                      />
                    </Col>
                    <Col xl={2}>
                      <Search
                        placeholder={props.t('email')}
                        value={qs?.get('email')?.replace(/%/g, '')}
                        onSubmit={({ id }) => {
                          const value = _.trim(id);
                          setSearchParamsAsObject({
                            email: id ? `%${value}%` : '',
                            page: 1,
                          });
                        }}
                      />
                    </Col>
                    <Col sm={1}>{isLoading && <Spinner color="primary" />}</Col>
                    <div className="col-sm-auto ms-auto">
                      <div className="hstack gap-2">
                        <Can I="create" a="account_users">
                          <button
                            type="button"
                            className="btn btn-success me-2"
                            onClick={() => setShowImporter(true)}
                          >
                            <i className="ri-upload-line align-bottom me-1"></i>
                            {props.t('deactivate-users-import')}
                          </button>
                        </Can>
                        <Can I="create" a="account_users">
                          <button
                            type="button"
                            className="btn btn-secondary add-btn"
                            id="create-btn"
                            onClick={() => setSearchParams('create', true)}
                          >
                            <i className="ri-add-line align-bottom me-1"></i>
                            {props.t('new-user')}
                          </button>
                        </Can>
                      </div>
                    </div>
                  </Row>
                </CardHeader>
                <CardBody className="pt-0">
                  <Can I="read" a="account_users">
                    <div>
                      <TableContainer
                        isLoading={isLoading}
                        columns={columns}
                        data={users || []}
                        activePage={searchParams.get('page')}
                        setPage={(p) => setSearchParams('page', p)}
                        pageSize={meta?.take || 10}
                        totalPages={meta?.pages || 0}
                        count={meta?.total || 0}
                        className="custom-header-css"
                        divClass="table-responsive table-card mb-0"
                        tableClass="align-middle table-nowrap"
                        theadClass="table-light"
                      />
                    </div>
                  </Can>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>

      <Drawer slug="create" className="w-25">
        <CreateUserForm onSuccess={getData} />
      </Drawer>

      <Drawer slug="edit" className="w-25">
        <EditUserForm onSuccess={getData} />
      </Drawer>

      <UserImporter
        isOpen={showImporter}
        toggle={() => setShowImporter(false)}
        onSuccess={getData}
      />
    </React.Fragment>
  );
};

export default withTranslation()(User);
